import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDatabase, ref, onValue, push, set, onDisconnect } from 'firebase/database';
import { getAuth } from 'firebase/auth';

const UserChat = () => {
  const [query, setQuery] = useState('');
  const [queries, setQueries] = useState([]);
  const [isAdminOnline, setIsAdminOnline] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  
  // Chatbot state
  const [chatbotMessages, setChatbotMessages] = useState([
    { sender: 'bot', text: 'Hello! How can we assist you with your immigration needs?' }
  ]);
  const [chatbotStep, setChatbotStep] = useState(0);
  const [userInfo, setUserInfo] = useState({
    email: '',
    phone: '',
    query: '',
    hasContactInfo: false,
    issueResolved: false
  });
  const [chatbotInput, setChatbotInput] = useState('');
  const [showChatbot, setShowChatbot] = useState(true);
  const [waitingForAdmin, setWaitingForAdmin] = useState(false);
  const [waitingTime, setWaitingTime] = useState(0);

  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  useEffect(() => {
    if (!userId) {
      toast.error('Please login first');
      window.location.href = '/login';
      return;
    }

    const database = getDatabase();

    // Set user as online
    const onlineRef = ref(database, `onlineUsers/${userId}`);
    set(onlineRef, {
      name: userName,
      lastSeen: new Date().toISOString()
    });

    // Remove from online when disconnected
    onDisconnect(onlineRef).remove();

    // Load user's queries
    loadUserQueries();
    // Load chat messages
    loadChatMessages();
    // Check admin online status
    checkAdminOnline();
  }, [userId, userName]);

  // Real-time chat message updates
  useEffect(() => {
    if (userId) {
      const database = getDatabase();
      const chatRef = ref(database, `chats/${userId}`);
      onValue(chatRef, (snapshot) => {
        const chatData = snapshot.val();
        if (chatData) {
          const messageList = Object.entries(chatData).map(([id, data]) => ({ id, ...data }));
          setChatMessages(messageList.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
          
          // Check if user already has contact info from previous messages
          const hasEmail = messageList.some(msg => msg.message && msg.message.includes('Email:'));
          const hasPhone = messageList.some(msg => msg.message && msg.message.includes('Phone:'));
          if (hasEmail || hasPhone) {
            setUserInfo(prev => ({ ...prev, hasContactInfo: true }));
          }
        }
      });
      
      // Check if user has existing queries with contact info
      const queriesRef = ref(database, 'queries');
      onValue(queriesRef, (snapshot) => {
        const queries = snapshot.val();
        if (queries) {
          const userQueries = Object.entries(queries).filter(([id, data]) => data.userId === userId);
          if (userQueries.length > 0) {
            const latestQuery = userQueries[userQueries.length - 1][1];
            if (latestQuery.email || latestQuery.phone) {
              setUserInfo(prev => ({ 
                ...prev, 
                hasContactInfo: true,
                email: latestQuery.email || '',
                phone: latestQuery.phone || ''
              }));
            }
          }
        }
      });
    }
  }, [userId]);

  // Start waiting timer when admin is offline
  useEffect(() => {
    if (chatbotStep >= 4 && !isAdminOnline) {
      setWaitingForAdmin(true);
      const timer = setInterval(() => {
        setWaitingTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setWaitingForAdmin(false);
      setWaitingTime(0);
    }
  }, [chatbotStep, isAdminOnline]);

  // Show unavailable message after 2-3 minutes
  useEffect(() => {
    if (waitingTime >= 180 && !isAdminOnline && chatbotStep >= 4) {
      const unavailableMessage = {
        sender: 'bot',
        text: 'Support team is not available now. We will reach you soon via email/phone. Thank you for your patience!'
      };
      setChatbotMessages(prev => {
        if (!prev.some(msg => msg.text === unavailableMessage.text)) {
          return [...prev, unavailableMessage];
        }
        return prev;
      });
    }
  }, [waitingTime, isAdminOnline, chatbotStep]);

  const loadUserQueries = () => {
    try {
      const database = getDatabase();
      const queriesRef = ref(database, 'queries');
      onValue(queriesRef, (snapshot) => {
        const queries = snapshot.val();
        const queryList = queries ? Object.entries(queries).map(([id, data]) => ({ id, ...data })) : [];
        const userQueries = queryList.filter(q => q.userId === userId);
        setQueries(userQueries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      });
    } catch (error) {
      console.error('Error loading queries:', error);
    }
  };

  const loadChatMessages = () => {
    // This is now handled by the real-time useEffect above
  };

  const checkAdminOnline = () => {
    try {
      const database = getDatabase();
      const adminOnlineRef = ref(database, 'adminOnline');
      onValue(adminOnlineRef, (snapshot) => {
        const adminOnline = snapshot.val();
        setIsAdminOnline(!!adminOnline);
      });
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const submitQuery = async () => {
    if (!query.trim()) {
      toast.error('Please enter your query');
      return;
    }

    try {
      const database = getDatabase();
      await push(ref(database, 'queries'), {
        userId: userId,
        userName: userName,
        message: query,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      
      setQuery('');
      toast.success('Query submitted successfully');
    } catch (error) {
      toast.error('Failed to submit query');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const database = getDatabase();
      await push(ref(database, `chats/${userId}`), {
        senderId: userId,
        senderName: userName,
        message: newMessage,
        timestamp: new Date().toISOString(),
        isAdmin: false
      });
      
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Chatbot functions
  const handleChatbotSubmit = async () => {
    if (!chatbotInput.trim()) return;

    const userMessage = chatbotInput;

    // Send message to Firebase for real-time sync
    try {
      const database = getDatabase();
      await push(ref(database, `chats/${userId}`), {
        senderId: userId,
        senderName: userName,
        message: userMessage,
        timestamp: new Date().toISOString(),
        isAdmin: false
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    }

    setChatbotMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
    setChatbotInput('');

    // Process based on current step
    if (chatbotStep < 7) {
      setTimeout(() => {
        let botResponse = '';
        let nextStep = chatbotStep;

        switch (chatbotStep) {
          case 0:
            setUserInfo(prev => ({ ...prev, initialMessage: userMessage }));
            // Skip contact info if already collected
            if (userInfo.hasContactInfo) {
              botResponse = 'Perfect! Please describe your immigration query in detail:';
              nextStep = 3;
            } else {
              botResponse = 'Thank you! Could you please provide your email address?';
              nextStep = 1;
            }
            break;
          case 1:
            if (validateEmail(userMessage)) {
              setUserInfo(prev => ({ ...prev, email: userMessage }));
              botResponse = 'Great! Now, could you please provide your phone number?';
              nextStep = 2;
            } else {
              botResponse = 'Please enter a valid email address. Try again:';
              nextStep = 1;
            }
            break;
          case 2:
            if (validatePhone(userMessage)) {
              setUserInfo(prev => ({ ...prev, phone: userMessage, hasContactInfo: true }));
              botResponse = 'Perfect! Please describe your immigration query in detail:';
              nextStep = 3;
            } else {
              botResponse = 'Please enter a valid phone number. Try again:';
              nextStep = 2;
            }
            break;
          case 3:
            setUserInfo(prev => ({ ...prev, query: userMessage }));
            botResponse = isAdminOnline ? 'Thank you! An admin is online and will join shortly.' : 'Thank you! Your information has been collected. An admin will join the chat shortly to assist you.';
            nextStep = 4;
            submitToAdmin();
            break;
          case 4:
            // After admin chat, ask if issue is resolved
            botResponse = 'Has your query been resolved? (Yes/No)';
            nextStep = 5;
            break;
          case 5:
            const isResolved = userMessage.toLowerCase().includes('yes');
            setUserInfo(prev => ({ ...prev, issueResolved: isResolved }));
            if (isResolved) {
              botResponse = 'Great! Is there anything else we can help you with?';
              nextStep = 6;
            } else {
              botResponse = 'I understand. Let me connect you with an admin for further assistance.';
              nextStep = 4;
              submitToAdmin();
            }
            break;
          case 6:
            if (userMessage.toLowerCase().includes('yes') || userMessage.toLowerCase().includes('help')) {
              botResponse = 'Please describe your new query:';
              nextStep = 3;
            } else {
              botResponse = 'Thank you for using Travel Immigration support. Have a great day!';
              nextStep = 7;
            }
            break;
          default:
            botResponse = 'Thank you for your patience. An admin will join soon.';
        }

        // Add bot message locally (not to Firebase)
        setChatbotMessages(prev => [...prev, { sender: 'bot', text: botResponse }]);
        setChatbotStep(nextStep);
      }, 500);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[0-9]{10,15}$/.test(phone.replace(/[\s-]/g, ''));

  const submitToAdmin = async () => {
    try {
      const database = getDatabase();
      await push(ref(database, 'queries'), {
        userId: userId,
        userName: userName,
        email: userInfo.email,
        phone: userInfo.phone,
        initialMessage: userInfo.initialMessage,
        query: userInfo.query,
        status: 'pending',
        createdAt: new Date().toISOString(),
        awaitingAdmin: true
      });

      // Also add to chat history
      await push(ref(database, `chats/${userId}`), {
        senderId: 'system',
        senderName: 'System',
        message: `New user query: ${userInfo.query}\nEmail: ${userInfo.email}\nPhone: ${userInfo.phone}`,
        timestamp: new Date().toISOString(),
        isAdmin: false,
        isSystem: true
      });

      toast.success('Information submitted to admin');
    } catch (error) {
      toast.error('Failed to submit to admin');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '20px' }}>
      <ToastContainer />
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>Help & Support</h1>
          
          {/* Admin Online Status */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: isAdminOnline ? '#D1FAE5' : '#FEE2E2',
            borderRadius: '8px'
          }}>
            <div style={{ 
              width: '10px', 
              height: '10px', 
              borderRadius: '50%', 
              backgroundColor: isAdminOnline ? '#10B981' : '#EF4444' 
            }}></div>
            <span style={{ fontSize: '14px', color: isAdminOnline ? '#065F46' : '#991B1B' }}>
              {isAdminOnline ? '🟢 Admin is online - Live chat available' : '🔴 Admin is offline - Leave a query'}
            </span>
            {waitingForAdmin && !isAdminOnline && (
              <div style={{ marginLeft: '8px', fontSize: '12px', color: '#991B1B' }}>
                Waiting: {Math.floor(waitingTime / 60)}:{(waitingTime % 60).toString().padStart(2, '0')}
                {waitingTime >= 180 && ' - Support team is not available now. We will reach you soon.'}
              </div>
            )}
          </div>

          {/* Chatbot Interface */}
          {showChatbot && (
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>Chat with our Assistant</h2>
              <div style={{ 
                border: '2px solid #E5E7EB', 
                borderRadius: '8px', 
                padding: '16px',
                backgroundColor: '#F9FAFB',
                marginBottom: '16px'
              }}>
                {/* Chat Messages */}
                <div style={{ 
                  height: '300px', 
                  overflowY: 'auto', 
                  marginBottom: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '8px'
                }}>
                  {chatbotMessages.map((msg, index) => (
                    <div
                      key={index}
                      style={{
                        maxWidth: '70%',
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: msg.sender === 'bot' ? '#3B82F6' : '#E5E7EB',
                        color: msg.sender === 'bot' ? 'white' : '#1F2937',
                        alignSelf: msg.sender === 'bot' ? 'flex-start' : 'flex-end'
                      }}
                    >
                      <div style={{ fontSize: '14px' }}>{msg.text}</div>
                    </div>
                  ))}
                </div>

                {/* Chat Input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={chatbotInput}
                    onChange={(e) => setChatbotInput(e.target.value)}
                    placeholder={chatbotStep >= 4 ? "Continue chatting with admin..." : "Type your message..."}
                    onKeyPress={(e) => e.key === 'Enter' && handleChatbotSubmit()}
                    disabled={chatbotStep >= 7}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleChatbotSubmit}
                    disabled={chatbotStep >= 7}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: chatbotStep >= 7 ? '#9CA3AF' : '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: chatbotStep >= 7 ? 'not-allowed' : 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Send
                  </button>
                </div>
              </div>

              {/* Progress Indicator */}
              {chatbotStep < 4 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {[0, 1, 2, 3].map((step) => (
                    <div
                      key={step}
                      style={{
                        flex: 1,
                        height: '4px',
                        backgroundColor: chatbotStep > step ? '#10B981' : chatbotStep === step ? '#3B82F6' : '#E5E7EB',
                        borderRadius: '2px'
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Step Description */}
              {chatbotStep < 4 && (
                <div style={{ fontSize: '14px', color: '#6B7280' }}>
                  {chatbotStep === 0 && 'Step 1: Tell us how we can help'}
                  {chatbotStep === 1 && 'Step 2: Provide your email'}
                  {chatbotStep === 2 && 'Step 3: Provide your phone number'}
                  {chatbotStep === 3 && 'Step 4: Describe your query'}
                </div>
              )}
            </div>
          )}

          {/* Live Chat Toggle */}
          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setShowChat(!showChat)}
              style={{
                padding: '12px 24px',
                backgroundColor: showChat ? '#dc2626' : '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {showChat ? 'Close Live Chat' : 'Open Live Chat'}
            </button>
          </div>

          {/* Live Chat */}
          {showChat && (
            <div style={{ 
              border: '2px solid #E5E7EB', 
              borderRadius: '8px', 
              padding: '16px',
              backgroundColor: '#F9FAFB'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Live Chat with Admin</h3>
              <div style={{ 
                height: '300px', 
                overflowY: 'auto', 
                marginBottom: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px'
              }}>
                {chatMessages.length > 0 ? (
                  chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        maxWidth: '70%',
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: msg.isAdmin ? '#10B981' : 
                                        msg.isSystem ? '#F59E0B' : '#E5E7EB',
                        color: msg.isAdmin ? 'white' : 
                               msg.isSystem ? 'white' : '#1F2937',
                        alignSelf: msg.isAdmin ? 'flex-start' : 
                                 msg.isSystem ? 'center' : 'flex-end'
                      }}
                    >
                      <div style={{ fontSize: '12px', marginBottom: '4px', opacity: 0.8 }}>
                        {msg.isAdmin ? 'Admin' : msg.isSystem ? 'System' : 'You'}
                      </div>
                      <div style={{ fontSize: '14px' }}>{msg.message}</div>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#6B7280', textAlign: 'center' }}>No messages yet. Start the conversation!</p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={sendMessage}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* My Queries */}
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>My Queries</h2>
            {queries.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {queries.map((q) => (
                  <div key={q.id} style={{ 
                    padding: '16px', 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: '8px', 
                    border: '1px solid #E5E7EB' 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        {new Date(q.createdAt).toLocaleString()}
                      </span>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        backgroundColor: q.status === 'pending' ? '#FEF3C7' : '#D1FAE5', 
                        color: q.status === 'pending' ? '#92400E' : '#065F46' 
                      }}>
                        {q.status}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#374151' }}>{q.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6B7280' }}>No queries submitted yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserChat;
